import React, {useCallback, useEffect} from 'react';
import {v4 as uuid} from 'uuid';
import {Stack} from '@mui/material';
import {
  Button,
  designSystemToken,
  Headline,
  Label,
} from '@lightricks/react-design-system';
import useUiInteractionEvent from '@contentlab/hooks/analytics/use-interaction-event';
import usePurchaseProcessStartedEvent from '@contentlab/hooks/analytics/use-purchase-process-started-event';
import useStoreViewDismissedEvent from '@contentlab/hooks/analytics/use-store-view-dismissed-event';
import useStoreViewPresentedEvent from '@contentlab/hooks/analytics/use-store-view-presented-event';
import AnalyticsService from '@/services/analytics/AnalyticsService';
import {
  CONTENT_STUDIO_PRICING_SOURCES,
  CONTENT_STUDIO_PRICING_VIEW_TYPES,
  CONTENT_STUDIO_VIEW_NAMES,
  FLOW_NAMES,
} from '@/lib/analytics/analyticsConstants';
import translate from '@/utils/translate';
import {ROUTES} from '@/config/routesConstants';
import ContentLabPlanBundle from '@/types/models/pricing/contentLabPlanBundle';
import {SubscriptionPlan} from '@/types/models/pricing/subscriptionPlan';
import {PricingCtaButton} from '@/views/content-studio/pricing/components/pricing-bundle/Buttons';
import DurationToggle from '@/views/content-studio/pricing/components/pricing-bundle/DurationToggle';
import Features from '@/views/content-studio/pricing/components/pricing-bundle/Features';
import PlanSelector from '@/views/content-studio/pricing/components/pricing-bundle/PlanSelector';
import PriceFootnote from '@/views/content-studio/pricing/components/pricing-bundle/PriceFootnote';
import Price from '@/views/content-studio/pricing/components/pricing-bundle/PriceSection';
import {DEFAULT_CURRENCY} from '@/views/content-studio/pricing/constants';
import Conditional from '@/components/conditional';
import Skeleton from '@/components/skeleton';
import useNavigation from '@/hooks/use-navigation';
import usePricingPlanSelection from '@/hooks/use-pricing-plan-selection/usePricingPlanSelection';
import styles from './UpgradeModalContent.module.scss';

type UpgradeModalContentProps = {
  isLoading: boolean;
  plans: SubscriptionPlan[];
  activePlan?: SubscriptionPlan;
  activeSubscriptionId?: string;
  handleClose: () => void;
  presentationId: string;
};

function UpgradeModalContent({
  isLoading,
  plans,
  activePlan,
  activeSubscriptionId,
  handleClose,
  presentationId,
}: UpgradeModalContentProps) {
  const {uiInteraction} = useUiInteractionEvent();
  const {purchaseProcessStarted} = usePurchaseProcessStartedEvent(
    FLOW_NAMES.CONTENT_STUDIO.PRICING
  );

  const navigation = useNavigation();

  const {storeViewPresented} = useStoreViewPresentedEvent(
    FLOW_NAMES.CONTENT_STUDIO.PRICING
  );
  const {storeViewDismissed} = useStoreViewDismissedEvent(
    FLOW_NAMES.CONTENT_STUDIO.PRICING
  );
  const sendUiInteractionEvent = useCallback(
    (buttonName: string) => {
      uiInteraction({
        item_name: buttonName,
        interaction_location: CONTENT_STUDIO_VIEW_NAMES.PRICING,
        flow_name: FLOW_NAMES.CONTENT_STUDIO.PRICING,
        presentation_id: presentationId,
        interaction_id: uuid(),
      });
    },
    [presentationId]
  );

  const sendPurchaseProcessStartedEvent = useCallback(
    (product_id: string, process_id: string) => {
      purchaseProcessStarted({
        presentation_id: presentationId,
        process_id,
        product_id,
        product_name: product_id,
      });
    },
    [presentationId]
  );

  const handleViewAllPlansClick = () => {
    uiInteraction({
      item_name: 'View All Plans',
      interaction_location: CONTENT_STUDIO_PRICING_VIEW_TYPES.PRICING_MODAL,
      flow_name: FLOW_NAMES.CONTENT_STUDIO.PRICING,
      presentation_id: presentationId,
      interaction_id: uuid(),
    });
    AnalyticsService.updateFlow(FLOW_NAMES.CONTENT_STUDIO.PRICING, {
      source: CONTENT_STUDIO_PRICING_SOURCES.PRICING_MODAL,
    });
    handleClose();
    navigation.navigate(ROUTES.CONTENT_LAB_PRICING);
  };

  const {
    selectedSubscriptionPlan,
    selectedDuration,
    presentedPlanList,
    handleDurationToggled,
    handlePlanSelected,
    handleSubscribeButtonClick,
    getOriginalPrice,
  } = usePricingPlanSelection({
    plans,
    activePlan,
    activeSubscriptionId,
    shouldSkipUpgradeModal: false,
    sendUiInteractionEvent,
    sendPurchaseProcessStartedEvent,
  });

  const originalPrice = getOriginalPrice();

  const getCtaButtonLocaleLabelKey = () => {
    if (activePlan?.bundle === ContentLabPlanBundle.STUDIO) {
      return 'Upgrade your Plan';
    }
    if (selectedSubscriptionPlan?.id === activePlan?.id) {
      return 'Current Plan';
    }
    return 'Upgrade';
  };

  const titleLocaleLabelKey =
    activePlan?.bundle === ContentLabPlanBundle.STUDIO
      ? 'Upgrade to a Business Plan'
      : 'Upgrade your Business Plan';

  useEffect(() => {
    storeViewPresented({
      presentation_id: presentationId,
      view_name: CONTENT_STUDIO_VIEW_NAMES.PRICING,
      view_type: CONTENT_STUDIO_PRICING_VIEW_TYPES.PRICING_MODAL,
      view_details: selectedSubscriptionPlan?.id,
    });
  }, []);

  return (
    <Stack direction="column" justifyContent="space-between" height="100%">
      <Conditional
        condition={!isLoading && selectedSubscriptionPlan !== undefined}
        fallback={<LoadingSkeleton />}
      >
        {selectedSubscriptionPlan && (
          <Stack gap="8px">
            <DurationToggle
              selectedDuration={selectedDuration}
              handleDurationChange={handleDurationToggled}
              isDisabled={activePlan?.bundle === ContentLabPlanBundle.BUSINESS}
            />

            <Headline size="md" className={styles.title}>
              {translate(titleLocaleLabelKey)}
            </Headline>

            <div className={styles.priceSection}>
              <Price
                bundle={ContentLabPlanBundle.BUSINESS}
                currency={selectedSubscriptionPlan.currency || DEFAULT_CURRENCY}
                price={selectedSubscriptionPlan.price}
                originalPrice={originalPrice}
              />
              <PriceFootnote
                selectedDuration={selectedDuration}
                selectedSubscriptionPlan={selectedSubscriptionPlan}
                className={styles.priceFootnoteText}
              />
            </div>

            <PlanSelector
              presentedPlanList={presentedPlanList}
              selectedSubscriptionPlan={selectedSubscriptionPlan}
              handlePlanChange={handlePlanSelected}
            />

            <Features
              bundle={ContentLabPlanBundle.BUSINESS}
              hasTitle={false}
              seatsCount={selectedSubscriptionPlan.editors}
              videosCount={selectedSubscriptionPlan.videos}
              classname={styles.features}
            />
          </Stack>
        )}
      </Conditional>

      <div className={styles.buttons}>
        <PricingCtaButton
          onClick={() => {
            storeViewDismissed({
              presentation_id: presentationId,
              view_name: CONTENT_STUDIO_VIEW_NAMES.PRICING,
              view_type: CONTENT_STUDIO_PRICING_VIEW_TYPES.PRICING_MODAL,
            });
            handleSubscribeButtonClick(getCtaButtonLocaleLabelKey());
          }}
          isHighlighted
          isDisabled={selectedSubscriptionPlan?.isDisabled || false}
          isCurrentPlan={selectedSubscriptionPlan?.id === activePlan?.id}
          isLoading={isLoading}
          text={translate(getCtaButtonLocaleLabelKey())}
        />
        <Button
          appearance="neutral"
          mode="plain"
          size="medium"
          onClick={handleViewAllPlansClick}
          style={{alignSelf: 'center', marginTop: '24px', height: '18px'}}
        >
          <Label
            size="md"
            color={designSystemToken('semantic.bg.neutral')}
            style={{fontSize: '12px', fontWeight: '700'}}
          >
            {translate('View All Plans')}
          </Label>
        </Button>
      </div>
    </Stack>
  );
}

function LoadingSkeleton() {
  return (
    <Stack direction="column">
      <Skeleton
        variant="rounded"
        width="100%"
        height="34px"
        borderRadius="4px"
      />
      <Skeleton
        variant="rounded"
        width="100%"
        height="26px"
        borderRadius="4px"
        margin="32px 0 0 0"
      />
      <Skeleton
        variant="rounded"
        width="244px"
        height="40px"
        borderRadius="4px"
        margin="8px 0 0 0"
      />
      <Skeleton
        variant="rounded"
        width="178px"
        height="18px"
        borderRadius="4px"
        margin="50px 0 0 0"
      />
      <Skeleton
        variant="rounded"
        width="178px"
        height="18px"
        borderRadius="4px"
        margin="12px 0 0 0"
      />
    </Stack>
  );
}

export default UpgradeModalContent;
